import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  pricing,
  products,
  type RateAdjustment,
  type RateAdjustmentInput,
  type RateBand,
  type RateBandInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Tabela de taxas.
 *
 * A faixa diz quanto pode ser cobrado por modalidade e por valor garantido. Ela
 * entra por vigencia e a anterior permanece: a taxa acordada numa operacao
 * passada precisa continuar explicavel meses depois.
 *
 * Nenhuma taxa vive no codigo da tela. Tudo aqui vem do servidor.
 */
export function RatesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [modalidade, setModalidade] = useState('');
  const [criando, setCriando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<RateBand | null>(null);

  const podeCriar = can('taxas.faixa.create');
  const podeAtualizar = can('taxas.faixa.update');

  const listaProdutos = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
  });

  const consulta = useQuery({
    queryKey: ['rate-bands', modalidade],
    queryFn: ({ signal }) => pricing.bands(modalidade || undefined, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['rate-bands'] });
  }

  const criar = useMutation({
    mutationFn: (input: RateBandInput) => pricing.createBand(input),
    onSuccess: () => {
      toast.success('Faixa de taxa criada.');
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: RateBandInput }) =>
      pricing.updateBand(id, input),
    onSuccess: () => {
      toast.success('Faixa atualizada.');
      setEmEdicao(null);
      invalidar();
    },
  });

  // As modalidades vem da arvore de oferta, e nao de uma lista mantida aqui.
  const modalidades = (listaProdutos.data ?? []).flatMap((produto) =>
    produto.modalities.map((modality) => ({
      value: modality.id,
      label: `${produto.name} - ${modality.name}`,
      productId: produto.id,
    })),
  );

  function nomeDaModalidade(modalityId: string): string {
    return modalidades.find((item) => item.value === modalityId)?.label ?? modalityId;
  }

  const colunas: Column<RateBand>[] = [
    {
      key: 'modalidade',
      header: 'Modalidade',
      render: (band) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">
            {nomeDaModalidade(band.modality_id)}
          </p>
          <p className="truncate text-2xs text-ink-500">
            Vigente desde {formatCivilDate(band.effective_from)}
          </p>
        </div>
      ),
    },
    {
      key: 'faixa',
      header: 'Faixa de valor',
      hideBelow: 'md',
      render: (band) => (
        <span className="text-xs">
          {formatMoney(band.min_amount)}
          {band.max_amount ? ` a ${formatMoney(band.max_amount)}` : ' ou mais'}
        </span>
      ),
    },
    {
      key: 'intervalo',
      header: 'Piso e teto',
      align: 'right',
      hideBelow: 'lg',
      render: (band) => (
        <span className="text-xs tabular-nums">
          {formatRate(band.min_rate_bps)} a {formatRate(band.max_rate_bps)}
        </span>
      ),
    },
    {
      key: 'base',
      header: 'Taxa base',
      align: 'right',
      render: (band) => (
        <span className="font-semibold tabular-nums">
          {formatRate(band.base_rate_bps)}
        </span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (band) => <StatusBadge kind="faixa" value={band.status} />,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Tabela de taxas"
        description="Piso, teto e ponto de partida por modalidade e faixa de valor."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>
              <Icon name="coins" size={15} />
              Nova faixa
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a tabela">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-3">
          <Select
            label="Modalidade"
            className="h-9"
            options={[{ value: '', label: 'Todas as modalidades' }, ...modalidades]}
            value={modalidade}
            onChange={(event) => setModalidade(event.target.value)}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data ?? []}
          keyOf={(band) => band.id}
          loading={consulta.isPending}
          onRowClick={podeAtualizar ? (band) => setEmEdicao(band) : undefined}
          empty={
            <EmptyState
              icon="coins"
              title="Nenhuma faixa cadastrada"
              description="Sem faixa vigente nao existe taxa aprovada, e a operacao nao pode ser precificada. Cadastre a primeira faixa da modalidade."
              action={
                podeCriar ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar faixa</Button>
                ) : null
              }
            />
          }
        />
      </Card>

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Nova faixa de taxa"
        description="A faixa entra por vigencia. A anterior permanece no historico."
        size="lg"
      >
        <RateBandForm
          modalidades={modalidades}
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      <Modal
        open={emEdicao !== null}
        onClose={() => setEmEdicao(null)}
        title="Editar faixa"
        description="A vigencia publicada nao muda: politica nova entra como faixa nova."
        size="lg"
      >
        {emEdicao ? (
          <RateBandForm
            band={emEdicao}
            modalidades={modalidades}
            submitting={atualizar.isPending}
            error={atualizar.error}
            onCancel={() => setEmEdicao(null)}
            onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function RateBandForm({
  band,
  modalidades,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  band?: RateBand | undefined;
  modalidades: { value: string; label: string; productId: string }[];
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: RateBandInput) => void;
  onCancel: () => void;
}) {
  const [modalityId, setModalityId] = useState(band?.modality_id ?? '');
  const [minAmount, setMinAmount] = useState(moneyToInput(band?.min_amount));
  const [maxAmount, setMaxAmount] = useState(moneyToInput(band?.max_amount));
  const [minRate, setMinRate] = useState(band ? rateToInput(band.min_rate_bps) : '');
  const [maxRate, setMaxRate] = useState(band ? rateToInput(band.max_rate_bps) : '');
  const [baseRate, setBaseRate] = useState(band ? rateToInput(band.base_rate_bps) : '');
  const [vigencia, setVigencia] = useState(band?.effective_from.slice(0, 10) ?? '');
  const [status, setStatus] = useState(band?.status ?? 'ativa');
  const [notes, setNotes] = useState(band?.notes ?? '');

  const apiError = error instanceof ApiError ? error : null;
  const erroMinimo = moneyInputError(minAmount);
  const erroMaximo = moneyInputError(maxAmount);
  const erroPiso = rateInputError(minRate);
  const erroTeto = rateInputError(maxRate);
  const erroBase = rateInputError(baseRate);
  const valorInvalido = [erroMinimo, erroMaximo, erroPiso, erroTeto, erroBase].some(
    (erro) => erro !== undefined,
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valorInvalido) return;

    const produto = modalidades.find((item) => item.value === modalityId);
    onSubmit({
      product_id: produto?.productId ?? '',
      modality_id: modalityId,
      min_amount: toMoney(minAmount || '0'),
      max_amount: maxAmount ? toMoney(maxAmount) : null,
      min_rate_bps: rateToBasisPoints(minRate),
      max_rate_bps: rateToBasisPoints(maxRate),
      base_rate_bps: rateToBasisPoints(baseRate),
      effective_from: vigencia,
      status,
      notes,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={submitting} className="flex flex-col gap-4">
        <Select
          label="Modalidade"
          required
          placeholder="Selecione a modalidade"
          options={modalidades}
          value={modalityId}
          disabled={band !== undefined}
          error={fieldErrorOf(apiError, 'modality_id')}
          onChange={(event) => setModalityId(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            label="Valor minimo (R$)"
            value={minAmount}
            error={erroMinimo ?? fieldErrorOf(apiError, 'min_amount')}
            onValueChange={setMinAmount}
          />
          <MoneyField
            label="Valor maximo (R$)"
            value={maxAmount}
            error={erroMaximo ?? fieldErrorOf(apiError, 'max_amount')}
            hint="Vazio significa sem teto."
            onValueChange={setMaxAmount}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Piso da taxa (%)"
            inputMode="decimal"
            value={minRate}
            error={erroPiso ?? fieldErrorOf(apiError, 'min_rate_bps')}
            onChange={(event) => setMinRate(event.target.value)}
          />
          <Field
            label="Teto da taxa (%)"
            inputMode="decimal"
            value={maxRate}
            error={erroTeto ?? fieldErrorOf(apiError, 'max_rate_bps')}
            onChange={(event) => setMaxRate(event.target.value)}
          />
          <Field
            label="Taxa base (%)"
            inputMode="decimal"
            value={baseRate}
            error={erroBase ?? fieldErrorOf(apiError, 'base_rate_bps')}
            hint="Precisa caber entre o piso e o teto."
            onChange={(event) => setBaseRate(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Vigente a partir de"
            type="date"
            required
            value={vigencia}
            disabled={band !== undefined}
            error={fieldErrorOf(apiError, 'effective_from')}
            onChange={(event) => setVigencia(event.target.value)}
          />
          <Select
            label="Situacao"
            options={[
              { value: 'ativa', label: 'Ativa' },
              { value: 'inativa', label: 'Inativa' },
            ]}
            value={status}
            onChange={(event) => setStatus(event.target.value as RateBand['status'])}
          />
        </div>

        <Textarea
          label="Observacoes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </fieldset>

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={submitting}
          disabled={modalityId === '' || valorInvalido}
        >
          {band ? 'Salvar' : 'Cadastrar faixa'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Agravos e descontos.
 *
 * O ajuste e em base de pontos, e nao em percentual do percentual. O sinal vem do
 * tipo: um agravo com valor negativo seria um desconto disfarcado.
 */
export function AdjustmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [criando, setCriando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<RateAdjustment | null>(null);

  const podeCriar = can('agravos.fator.create');
  const podeAtualizar = can('agravos.fator.update');

  const consulta = useQuery({
    queryKey: ['rate-adjustments'],
    queryFn: ({ signal }) => pricing.adjustments(signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['rate-adjustments'] });
  }

  const criar = useMutation({
    mutationFn: (input: RateAdjustmentInput) => pricing.createAdjustment(input),
    onSuccess: () => {
      toast.success('Fator cadastrado.');
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: RateAdjustmentInput }) =>
      pricing.updateAdjustment(id, input),
    onSuccess: () => {
      toast.success('Fator atualizado.');
      setEmEdicao(null);
      invalidar();
    },
  });

  const colunas: Column<RateAdjustment>[] = [
    {
      key: 'fator',
      header: 'Fator',
      render: (adjustment) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{adjustment.label}</p>
          {adjustment.description ? (
            <p className="truncate text-2xs text-ink-500">{adjustment.description}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (adjustment) => <StatusBadge kind="ajuste" value={adjustment.kind} />,
    },
    {
      key: 'ajuste',
      header: 'Ajuste na taxa',
      align: 'right',
      render: (adjustment) => (
        <span
          className={
            adjustment.signed_bps < 0
              ? 'font-semibold tabular-nums text-success-700'
              : 'font-semibold tabular-nums text-warning-700'
          }
        >
          {adjustment.signed_bps < 0 ? '-' : '+'}
          {formatRate(adjustment.delta_bps)}
        </span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (adjustment) => <StatusBadge kind="cadastro" value={adjustment.status} />,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Agravos e descontos"
        description="Fatores que sobem ou descem a taxa base, sempre dentro do piso e do teto da faixa."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>
              <Icon name="coins" size={15} />
              Novo fator
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os fatores">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Alert tone="info" title="Como o fator entra na conta">
        O ajuste soma sobre a taxa base da faixa vigente. O resultado nunca passa do
        piso nem do teto: eles sao a alcada aprovada, e ultrapassa-los e decisao que
        precisa de aprovacao, e nao de aritmetica.
      </Alert>

      <Card>
        <DataTable
          columns={colunas}
          items={consulta.data ?? []}
          keyOf={(adjustment) => adjustment.id}
          loading={consulta.isPending}
          onRowClick={
            podeAtualizar ? (adjustment) => setEmEdicao(adjustment) : undefined
          }
          empty={
            <EmptyState
              icon="coins"
              title="Nenhum fator cadastrado"
              description="Sem fator, a taxa sugerida e a taxa base da faixa. Cadastre os agravos e descontos que a empresa pratica."
              action={
                podeCriar ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar fator</Button>
                ) : null
              }
            />
          }
        />
      </Card>

      <Modal open={criando} onClose={() => setCriando(false)} title="Novo fator">
        <AdjustmentForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      <Modal
        open={emEdicao !== null}
        onClose={() => setEmEdicao(null)}
        title={emEdicao?.label ?? ''}
      >
        {emEdicao ? (
          <AdjustmentForm
            adjustment={emEdicao}
            submitting={atualizar.isPending}
            error={atualizar.error}
            onCancel={() => setEmEdicao(null)}
            onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function AdjustmentForm({
  adjustment,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  adjustment?: RateAdjustment | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: RateAdjustmentInput) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState(adjustment?.kind ?? 'agravo');
  const [label, setLabel] = useState(adjustment?.label ?? '');
  const [description, setDescription] = useState(adjustment?.description ?? '');
  const [delta, setDelta] = useState(
    adjustment ? rateToInput(adjustment.delta_bps) : '',
  );
  const [status, setStatus] = useState(adjustment?.status ?? 'ativo');

  const apiError = error instanceof ApiError ? error : null;
  const erroDelta = rateInputError(delta);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (erroDelta) return;
    onSubmit({
      kind,
      label,
      description,
      delta_bps: rateToBasisPoints(delta),
      status,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={submitting} className="flex flex-col gap-4">
        <Field
          label="Nome do fator"
          required
          value={label}
          error={fieldErrorOf(apiError, 'label')}
          placeholder="Empresa com menos de dois anos de constituicao"
          onChange={(event) => setLabel(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Tipo"
            options={[
              { value: 'agravo', label: 'Agravo' },
              { value: 'desconto', label: 'Desconto' },
            ]}
            value={kind}
            hint="O sinal vem daqui."
            onChange={(event) => setKind(event.target.value as RateAdjustment['kind'])}
          />
          <Field
            label="Ajuste (%)"
            required
            inputMode="decimal"
            value={delta}
            error={erroDelta ?? fieldErrorOf(apiError, 'delta_bps')}
            hint="Sempre positivo."
            onChange={(event) => setDelta(event.target.value)}
          />
          <Select
            label="Situacao"
            options={[
              { value: 'ativo', label: 'Ativo' },
              { value: 'inativo', label: 'Inativo' },
            ]}
            value={status}
            hint="Fator inativo nao entra na conta."
            onChange={(event) =>
              setStatus(event.target.value as RateAdjustment['status'])
            }
          />
        </div>

        <Textarea
          label="Descricao"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </fieldset>

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={submitting} disabled={erroDelta !== undefined}>
          {adjustment ? 'Salvar' : 'Cadastrar fator'}
        </Button>
      </div>
    </form>
  );
}
